import {
  Button,
  Modal,
  SelectInput,
  ButtonsWrapper,
  CheckboxSelect,
} from '@thinknet-gmbh/platform-common-ui';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GanttAccessRightsEnum } from '@thinknet-gmbh/platform-common';

import { useGetGantts } from 'src/hooks/gantts/get-gantts.hook';
import { mapBuilder } from 'src/common/helpers/common.helpers';
import { useGetPermissionsGroups } from 'src/hooks/permission-group/get-permissions-groups.hook';
import { useUpdateMemberPermissionGroupAndGanttAccess } from 'src/hooks/members/update-member-permission-group.hook';

import { GanttRightsListSelect } from './gantt-rights-list-select';
import { IGanttsAccessPayload } from './change-user-permission.interfaces';
import { SelectLabel, FieldContainer } from './change-user-permission.styles';

interface IProps {
  onClose: () => void;
  selectedUser: any;
  companyId?: string;
}

const ChangeUserPermission: FC<IProps> = ({ onClose, selectedUser, companyId }) => {
  const { t } = useTranslation(['common', 'settings']);

  const [permissionGroupId, setPermissionGroup] = useState(selectedUser.account.permissionGroupId);
  const [ganttsAccessPayload, setGanttsAccessPayload] = useState<IGanttsAccessPayload[]>(
    selectedUser.ganttsAccess
      ? selectedUser.ganttsAccess.map((doc: any) => ({
          ganttId: doc.ganttId,
          accessRight: doc.accessRight,
        }))
      : []
  );

  const { loading, gantts } = useGetGantts();
  const { permissionsGroups } = useGetPermissionsGroups(companyId);
  const updateMemberPermissionGroupAndGanttAccess = useUpdateMemberPermissionGroupAndGanttAccess({
    onSuccess: onClose,
  });

  const onSubmitEditedPermission = () =>
    updateMemberPermissionGroupAndGanttAccess({
      variables: {
        ganttsAccessPayload,
        permissionGroupPayload: {
          permissionGroupId,
          memberId: selectedUser._id,
        },
      },
    });

  const onChangeSelect = (value?: string) => value && setPermissionGroup(value);
  const onRightChange = (index: number) => (newRight: string) =>
    setGanttsAccessPayload((prevData: IGanttsAccessPayload[]) => {
      const newData = [...prevData];

      newData[index].accessRight = newRight;

      return newData;
    });

  const onGanttRightRemove = (ganttId: string) => () =>
    setGanttsAccessPayload(ganttsAccessPayload.filter((doc: any) => doc.ganttId !== ganttId));

  return (
    <Modal isOpened onClose={onClose} title={t('settings:userManagementSettings.editModal.title')}>
      <FieldContainer>
        <SelectLabel htmlFor="permission">
          {t('settings:userManagementSettings.labels.permissionGroup')}
        </SelectLabel>
        <SelectInput id="permission" defaultValue={permissionGroupId} onChange={onChangeSelect}>
          {permissionsGroups.map((group: any) => (
            <SelectInput.Option key={group._id} value={group._id}>
              {group.name}
            </SelectInput.Option>
          ))}
        </SelectInput>
      </FieldContainer>

      <FieldContainer>
        <SelectLabel htmlFor="ganttsAccess">
          {t('settings:userManagementSettings.labels.availableGantts')}
        </SelectLabel>
        <CheckboxSelect
          showSearch
          id="ganttsAccess"
          loading={loading}
          placeholder={t('common:placeholders.selectFromList')}
          optionData={gantts.map(({ _id, name }: any) => ({ value: _id, label: name }))}
          value={ganttsAccessPayload.map(({ ganttId }) => ganttId)}
          onChange={ganttIds =>
            setGanttsAccessPayload(prevData => {
              const prevDataMap = mapBuilder(prevData, 'ganttId');

              return ganttIds.map((ganttId: string) => ({
                ganttId,
                accessRight: prevDataMap[ganttId]
                  ? prevDataMap[ganttId].accessRight
                  : GanttAccessRightsEnum.EDIT,
              }));
            })
          }
        />
      </FieldContainer>

      {ganttsAccessPayload.map(({ ganttId, accessRight }: any, index) => {
        const { name } = gantts.find((gantt: any) => gantt._id === ganttId) || {};

        return (
          <GanttRightsListSelect
            key={ganttId}
            selectedRight={accessRight}
            isViewSeparator={index !== ganttsAccessPayload.length - 1}
            onRightChange={onRightChange(index)}
            onGanttRightRemove={onGanttRightRemove(ganttId)}
            ganttName={name}
          />
        );
      })}

      <ButtonsWrapper marginTop={12}>
        <Button type="primary" onClick={onSubmitEditedPermission}>
          {t('common:buttons.save')}
        </Button>
        <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Modal>
  );
};

export default ChangeUserPermission;
